import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Shield, Plus, Trash2, ToggleLeft, ToggleRight, Loader2, Pencil, X, Save, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RULE_TYPES = [
  { value: 'supplier_match', label: 'Supplier Contains', desc: 'Match when supplier name contains text' },
  { value: 'description_contains', label: 'Description Contains', desc: 'Match when description contains text' },
  { value: 'amount_range', label: 'Amount Range', desc: 'Match when amount is within range' },
];

const RuleForm = ({ rule, onSave, onCancel, saving }) => {
  const [form, setForm] = useState({
    rule_type: rule?.rule_type || 'supplier_match',
    match_value: rule?.match_value || '',
    category_l1: rule?.category_l1 || '',
    category_l2: rule?.category_l2 || '',
    category_l3: rule?.category_l3 || '',
    category_l4: rule?.category_l4 || '',
    priority: rule?.priority ?? 10,
    amount_min: rule?.amount_min ?? '',
    amount_max: rule?.amount_max ?? '',
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const isAmountRange = form.rule_type === 'amount_range';

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4" data-testid="rule-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">Rule Type</label>
          <select value={form.rule_type} onChange={e => set('rule_type', e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="rule-type-select">
            {RULE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <p className="text-xs text-slate-400 mt-1">{RULE_TYPES.find(t => t.value === form.rule_type)?.desc}</p>
        </div>
        {isAmountRange ? (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Min Amount</label>
              <input type="number" value={form.amount_min} onChange={e => set('amount_min', e.target.value ? Number(e.target.value) : '')}
                placeholder="0" className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="rule-amount-min" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Max Amount</label>
              <input type="number" value={form.amount_max} onChange={e => set('amount_max', e.target.value ? Number(e.target.value) : '')}
                placeholder="No limit" className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="rule-amount-max" />
            </div>
          </div>
        ) : (
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Match Value</label>
            <input value={form.match_value} onChange={e => set('match_value', e.target.value)}
              placeholder={form.rule_type === 'supplier_match' ? 'e.g. Staples' : 'e.g. printer'}
              className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="rule-match-value" />
          </div>
        )}
      </div>

      <div className="border-t border-slate-100 pt-4">
        <p className="text-xs font-semibold text-slate-600 mb-2">Override Classification (L1-L4)</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {['category_l1', 'category_l2', 'category_l3', 'category_l4'].map((k, i) => (
            <div key={k}>
              <label className="block text-xs text-slate-500 mb-1">L{i + 1}{i === 0 ? ' *' : ''}</label>
              <input value={form[k]} onChange={e => set(k, e.target.value)}
                placeholder={['Broad Category', 'Sub Category', 'Detail Class', 'Specific Type'][i]}
                className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid={`rule-${k}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-slate-600">Priority:</label>
          <input type="number" min="1" max="100" value={form.priority}
            onChange={e => set('priority', parseInt(e.target.value) || 10)}
            className="w-20 px-2 py-1.5 border border-slate-200 rounded-sm text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="rule-priority" />
          <span className="text-xs text-slate-400">Lower = higher priority</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onCancel} data-testid="rule-cancel-btn">
            <X size={14} /> Cancel
          </Button>
          <Button size="sm" onClick={() => onSave(form)} disabled={saving}
            className="bg-[#0F172A] hover:bg-[#1e293b] text-white gap-1" data-testid="rule-save-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {rule ? 'Update Rule' : 'Create Rule'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const RuleTypeTag = ({ type }) => {
  const styles = {
    supplier_match: 'bg-blue-50 text-blue-700',
    description_contains: 'bg-purple-50 text-purple-700',
    amount_range: 'bg-emerald-50 text-emerald-700',
  };
  const labels = { supplier_match: 'Supplier', description_contains: 'Description', amount_range: 'Amount' };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${styles[type]}`}>{labels[type]}</span>;
};

export const ClassificationRules = ({ token }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchRules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/rules`, { headers: { Authorization: `Bearer ${token}` } });
      setRules(res.data.rules || []);
    } catch (err) {
      toast.error('Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const handleSave = async (form) => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (form.rule_type !== 'amount_range') {
        delete payload.amount_min;
        delete payload.amount_max;
      } else {
        payload.amount_min = form.amount_min === '' ? null : form.amount_min;
        payload.amount_max = form.amount_max === '' ? null : form.amount_max;
      }

      if (editingRule) {
        await axios.put(`${API}/rules/${editingRule.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Rule updated');
      } else {
        await axios.post(`${API}/rules`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Rule created');
      }
      setShowForm(false);
      setEditingRule(null);
      fetchRules();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (rule) => {
    try {
      await axios.put(`${API}/rules/${rule.id}`, { enabled: !rule.enabled }, { headers: { Authorization: `Bearer ${token}` } });
      setRules(prev => prev.map(r => r.id === rule.id ? { ...r, enabled: !r.enabled } : r));
    } catch { toast.error('Failed to toggle rule'); }
  };

  const handleDelete = async (ruleId) => {
    try {
      await axios.delete(`${API}/rules/${ruleId}`, { headers: { Authorization: `Bearer ${token}` } });
      setRules(prev => prev.filter(r => r.id !== ruleId));
      toast.success('Rule deleted');
    } catch { toast.error('Failed to delete rule'); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 size={32} className="animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="classification-rules-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-[#0F172A] mb-2">Classification Rules</h1>
          <p className="text-slate-600">Create persistent rules to override AI classifications during processing</p>
        </div>
        {!showForm && !editingRule && (
          <Button onClick={() => setShowForm(true)}
            className="bg-[#0F172A] hover:bg-[#1e293b] text-white gap-1.5" data-testid="add-rule-btn">
            <Plus size={16} /> Add Rule
          </Button>
        )}
      </div>

      {(showForm || editingRule) && (
        <RuleForm rule={editingRule} onSave={handleSave} saving={saving}
          onCancel={() => { setShowForm(false); setEditingRule(null); }} />
      )}

      {rules.length === 0 && !showForm ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Shield size={48} className="text-slate-300 mx-auto mb-3" />
            <p className="text-lg font-medium text-slate-700">No classification rules yet</p>
            <p className="text-sm text-slate-500 mt-1 mb-4">
              Rules automatically override AI classifications when conditions match.
            </p>
            <Button onClick={() => setShowForm(true)} className="bg-[#0F172A] hover:bg-[#1e293b] text-white gap-1.5" data-testid="add-first-rule-btn">
              <Plus size={16} /> Create Your First Rule
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="rules-list">
          {rules.map(rule => (
            <div key={rule.id} data-testid={`rule-row-${rule.id}`}
              className={`bg-white border rounded-sm p-4 transition-all ${
                rule.enabled ? 'border-slate-200' : 'border-slate-100 opacity-60'
              }`}>
              <div className="flex items-center gap-4">
                <button onClick={() => handleToggle(rule)} className="flex-shrink-0" data-testid={`rule-toggle-${rule.id}`}>
                  {rule.enabled
                    ? <ToggleRight size={28} className="text-green-500" />
                    : <ToggleLeft size={28} className="text-slate-300" />}
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <RuleTypeTag type={rule.rule_type} />
                    <span className="text-sm font-semibold text-slate-900">
                      {rule.rule_type === 'amount_range'
                        ? `$${rule.amount_min ?? 0} - $${rule.amount_max ?? '∞'}`
                        : `"${rule.match_value}"`}
                    </span>
                    <ChevronDown size={12} className="text-slate-400" />
                    <span className="text-sm text-blue-700 font-medium bg-blue-50 px-2 py-0.5 rounded-sm">
                      {[rule.category_l1, rule.category_l2, rule.category_l3, rule.category_l4].filter(Boolean).join(' > ')}
                    </span>
                  </div>
                  <span className="text-xs text-slate-400">Priority: {rule.priority}</span>
                </div>

                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button onClick={() => { setEditingRule(rule); setShowForm(false); }}
                    className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-sm transition-colors"
                    data-testid={`rule-edit-${rule.id}`}>
                    <Pencil size={15} />
                  </button>
                  <button onClick={() => handleDelete(rule.id)}
                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-sm transition-colors"
                    data-testid={`rule-delete-${rule.id}`}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {rules.length > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
          <p className="text-xs text-slate-600">
            <span className="font-semibold">How rules work:</span> During classification, rules are checked in priority order (lowest number first).
            The first matching rule overrides the AI classification with a 100% confidence score.
            Rules are applied after AI classification completes.
          </p>
        </div>
      )}
    </div>
  );
};
